import {
  LineChart,
  Line,
  BarChart,
  Bar,
  Area,
  Cell,
  LabelList,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useMemo, useState } from 'react';
import {
  axisProps,
  currencyPrefix,
  fmtAxis,
  fmtFull,
  gridProps,
  seriesColor,
  tooltipProps,
} from '../charts/tokens';
import {
  getForecastContext,
  type AnnualSummaryComputed,
  type ForecastContext,
  type QuarterlyData,
  type AnnualSummaryYear,
} from '../types/financial-model';

/**
 * The series the charts need. Each workspace maps its own statements onto this shape once,
 * at the boundary, rather than the charts carrying a branch per series.
 */
export interface ChartsData {
  ctx: ForecastContext;
  /** The model's reporting currency, so figures carry their unit. */
  currency?: string;
  annualSummary: AnnualSummaryComputed | null | undefined;
  totalRevenue: QuarterlyData;
  grossProfit: QuarterlyData;
  ebitda: QuarterlyData;
  profitAfterTax: QuarterlyData;
  closingCash: QuarterlyData;
  netOperatingCashflow: QuarterlyData;
  netInvestingCashflow: QuarterlyData;
  netFinancingCashflow: QuarterlyData;
  fixedAssetsNet: QuarterlyData;
  totalCurrentAssets: QuarterlyData;
  /** Plotted against total assets: the two diverge exactly when the sheet does not balance. */
  totalLiabilitiesAndEquity: QuarterlyData;
  /**
   * Whether to plot liabilities + equity against total assets.
   *
   * In v1 that comparison is informative: cash is a plug, so the two CAN
   * diverge, and seeing them apart is how you find out. In v2 cash comes from
   * the cashflow and the balance check is computed honestly on its own line,
   * so the series is identical to total assets by construction — a line that
   * can never differ tells a reader nothing and crowds the chart.
   */
  showBalanceReconciliation?: boolean;
  /** Stock, for inventory days. */
  stocks?: QuarterlyData;
  /** The deterministic valuation, as a reference against the sampled range. */
  dcfEnterpriseValue?: number | null;
  /**
   * The sampled enterprise value, when a probabilistic forecast has been run.
   * Absent until then, and the chart says so rather than drawing an empty box.
   */
  /**
   * Enterprise value under each deterministic sensitivity scenario, and under
   * the base case.
   *
   * The fallback for the two valuation charts. Both used to key off the
   * sampled forecast alone, so on any model that had never had an AI forecast
   * run against it - which is most of them - the two cards at the top of the
   * page were permanently empty.
   *
   * These come from the model's own scenarios, so the assumptions are stated
   * rather than invented: the label carries the shock that produced the
   * figure. v1 leaves this undefined and keeps its existing empty states.
   */
  scenarioValuations?: { label: string; enterpriseValue: number }[];
  baseValuation?: number | null;
  evDistribution?: {
    p5: number; p25: number; p50: number; p75: number; p95: number;
    mean: number; std: number;
  } | null;
  /** Driver -> effect on enterprise value, for the tornado. */
  driverSensitivity?: Record<string, number> | null;
  tradeDebtors: QuarterlyData;
  tradeCreditors: QuarterlyData;
  /** What the company owns, what it owes, and what is left: the net assets chart. */
  totalAssets: QuarterlyData;
  totalLiabilities: QuarterlyData;
  netAssets: QuarterlyData;
}

// ─── Colors ──────────────────────────────────────────────────

/**
 * Series colours, by role, from the validated palette in charts/tokens.ts.
 *
 * The previous palette was Tailwind's default 500s picked per series. It
 * failed four of the six colour checks — orange against red was ΔE 10.4 for a
 * reader with normal colour vision, below the floor at which two series are
 * distinguishable, and grey read as a gridline rather than data.
 *
 * A measure keeps its colour everywhere it appears, so the mapping is learned
 * once: revenue is always the blue, costs always the orange.
 */
const COLORS = {
  revenue: seriesColor('revenue'),
  grossProfit: seriesColor('cash'),
  ebitda: seriesColor('capital'),
  pat: seriesColor('profit'),
  cogs: seriesColor('cost'),
  indirect: seriesColor('capital'),
  depreciation: seriesColor('liability'),
  interest: seriesColor('liability'),
  tax: seriesColor('cost'),
  cash: seriesColor('cash'),
  opCashflow: seriesColor('cash'),
  invCashflow: seriesColor('cost'),
  finCashflow: seriesColor('capital'),
  fixedAssets: seriesColor('capital'),
  currentAssets: seriesColor('cash'),
  liabEquity: seriesColor('liability'),
  debtors: seriesColor('revenue'),
  creditors: seriesColor('cost'),
  nwc: seriesColor('capital'),
  assets: seriesColor('revenue'),
  liabilities: seriesColor('cost'),
  netAssets: seriesColor('profit'),
  grossMargin: seriesColor('cash'),
  ebitdaMargin: seriesColor('capital'),
  netMargin: seriesColor('profit'),
};

// ─── Helpers ─────────────────────────────────────────────────

/** Baseline Accounting's grid - dashed, both ways - in the theme's grid colour. */
const grid = () => ({ ...gridProps(), strokeDasharray: '3 3', vertical: true });

/**
 * Bar widths. Recharts fills each period's whole slot by default, so with four or five years a
 * bar ran a sixth of the chart wide. Capped, with air between periods: thinner where several
 * series share a period, wider where a period or a bridge step is one bar.
 */
const GROUPED_BARS = { maxBarSize: 18, barGap: 2, barCategoryGap: '28%' } as const;
const SINGLE_BARS = { maxBarSize: 40, barCategoryGap: '30%' } as const;

const MONTH_ABBREVS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function fmtQuarter(q: string): string {
  const [prefix, year] = q.split('_');
  if (prefix.startsWith('M')) {
    const monthIdx = parseInt(prefix.slice(1), 10) - 1;
    return `${MONTH_ABBREVS[monthIdx]} '${year.slice(2)}`;
  }
  if (prefix === 'FY') return year;
  return `${prefix} '${year.slice(2)}`;
}

function toQuarterlyChartData(
  ctx: ForecastContext,
  sources: Record<string, QuarterlyData>,
): Record<string, string | number>[] {
  return ctx.allQuarters.map((q) => {
    const row: Record<string, string | number> = { quarter: fmtQuarter(q) };
    for (const [key, data] of Object.entries(sources)) {
      row[key] = data[q] ?? 0;
    }
    return row;
  });
}

function fmtValue(v: number): string {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
  return v.toFixed(0);
}

function fmtTooltip(v: number | undefined): string {
  return (v ?? 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/**
 * The rows behind a chart, for its table view.
 *
 * Built from the same array the chart plots, so the two can never disagree —
 * a table assembled separately drifts the first time a series changes.
 */
function seriesRows(
  data: Record<string, string | number>[],
  series: { key: string; name: string }[],
  labelKey: string,
): { label: string; values: (number | string)[] }[] {
  return [
    { label: '', values: data.map((r) => String(r[labelKey])) },
    ...series.map((sx) => ({
      label: sx.name,
      values: data.map((r) => r[sx.key] as number),
    })),
  ];
}

// ─── Chart Card ──────────────────────────────────────────────

function ChartCard({
  title,
  subtitle,
  rows,
  currency,
  control,
  children,
}: {
  title: string;
  subtitle?: string;
  /** The same numbers the chart plots. Required: see the note on contrast. */
  rows?: { label: string; values: (number | string)[] }[];
  columns?: string[];
  currency?: string;
  /** An optional filter for this chart, shown beside the table toggle. */
  control?: React.ReactNode;
  children: React.ReactNode;
}) {
  const [showTable, setShowTable] = useState(false);

  return (
    // Baseline Accounting's card: a plain bordered panel, a small medium-weight title.
    <div className="chart-card rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div>
          <h3 className="chart-card-title text-sm font-medium">{title}</h3>
          {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {control}
          {rows && (
          <button
            type="button"
            onClick={() => setShowTable((v) => !v)}
            className="shrink-0 rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-50"
            aria-pressed={showTable}
          >
            {showTable ? 'Chart' : 'Table'}
          </button>
          )}
        </div>
      </div>
      <div className="chart-plot h-[300px]">
        {showTable && rows ? (
          <div className="h-full overflow-auto">
            <table className="w-full text-xs">
              <tbody>
                {rows.map((r) => (
                  <tr key={r.label} className="border-b border-gray-100">
                    <th scope="row" className="py-1 pr-3 text-left font-medium text-gray-700">
                      {r.label}
                    </th>
                    {r.values.map((v, i) => (
                      <td key={i} className="py-1 text-right tabular-nums text-gray-600">
                        {typeof v === 'number' ? fmtFull(v, currency) : v}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

// ─── Individual Charts ───────────────────────────────────────

function RevenueProfitChart({ d }: { d: ChartsData }) {
  const data = toQuarterlyChartData(d.ctx, {
    totalRevenue: d.totalRevenue,
    grossProfit: d.grossProfit,
    ebitda: d.ebitda,
    profitAfterTax: d.profitAfterTax,
  });

  return (
    <ChartCard
      title="Revenue and profit"
      subtitle="Revenue down to profit after tax, each period"
      currency={d.currency}
      rows={seriesRows(data, [
        { key: 'totalRevenue', name: 'Revenue' },
        { key: 'grossProfit', name: 'Gross Profit' },
        { key: 'ebitda', name: 'EBITDA' },
        { key: 'profitAfterTax', name: 'PAT' },
      ], 'quarter')}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart {...GROUPED_BARS} data={data} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
          <CartesianGrid {...grid()} />
          <XAxis dataKey="quarter" {...axisProps()} />
          <YAxis tickFormatter={fmtAxis} width={58} {...axisProps()} />
          <Tooltip formatter={(v: number | undefined) => fmtFull(v, d.currency)} {...tooltipProps()} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="totalRevenue" name="Revenue" fill={COLORS.revenue} />
          <Bar dataKey="grossProfit" name="Gross Profit" fill={COLORS.grossProfit} />
          <Bar dataKey="ebitda" name="EBITDA" fill={COLORS.ebitda} />
          <Bar dataKey="profitAfterTax" name="PAT" fill={COLORS.pat} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

/**
 * Revenue-to-profit bridge for ONE year.
 *
 * It replaces a grouped bar chart that put seven series across five years on
 * one axis: 35 bars, several of them hairlines against a revenue bar two
 * orders of magnitude taller. A bridge answers the question that chart was
 * reaching for — what consumes revenue — and a bridge is inherently a
 * single-period form, so the year is chosen rather than crammed in.
 *
 * Drawn as a stacked bar with an invisible base, which is how a waterfall is
 * built when the chart library has no waterfall mark.
 */
function PnLBridgeChart({ d }: { d: ChartsData }) {
  const years = d.ctx.fiscalYears;
  const [year, setYear] = useState(() => years[years.length - 1]);
  const y = d.annualSummary?.years[year];

  const steps: { name: string; amount: number; kind: 'start' | 'cost' | 'total' }[] = [
    { name: 'Revenue', amount: y?.totalRevenue ?? 0, kind: 'start' },
    { name: 'COGS', amount: -(y?.cogs ?? 0), kind: 'cost' },
    { name: 'Indirect', amount: -(y?.totalIndirectCosts ?? 0), kind: 'cost' },
    { name: 'Depn', amount: -(y?.depreciation ?? 0), kind: 'cost' },
    { name: 'Interest', amount: -(y?.interestExpense ?? 0), kind: 'cost' },
    { name: 'Tax', amount: -(y?.corporationTax ?? 0), kind: 'cost' },
  ];

  let running = 0;
  const data = steps.map((st) => {
    const base = st.amount >= 0 ? running : running + st.amount;
    running += st.amount;
    return {
      name: st.name,
      base,
      delta: Math.abs(st.amount),
      signed: st.amount,
      kind: st.kind,
    };
  });
  data.push({ name: 'PAT', base: 0, delta: running, signed: running, kind: 'total' });

  const fillFor = (kind: string) =>
    kind === 'start' ? COLORS.revenue : kind === 'total' ? COLORS.pat : COLORS.cogs;

  return (
    <ChartCard
      title="Revenue to profit"
      subtitle={`What consumes revenue in ${year}`}
      currency={d.currency}
      rows={seriesRows(
        data as unknown as Record<string, string | number>[],
        [{ key: 'signed', name: 'Amount' }],
        'name',
      )}
      control={
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-700"
          aria-label="Year"
        >
          {years.map((fy) => (
            <option key={fy} value={fy}>
              {fy}
            </option>
          ))}
        </select>
      }
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart {...SINGLE_BARS} data={data} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
          <CartesianGrid {...grid()} />
          <XAxis dataKey="name" {...axisProps()} />
          <YAxis tickFormatter={fmtAxis} width={58} {...axisProps()} />
          <Tooltip
            {...tooltipProps()}
            formatter={(_v, _n, item) => [
              fmtFull((item?.payload as { signed?: number })?.signed, d.currency),
              'Amount',
            ]}
          />
          {/* The invisible half of the stack: it positions each bar, and must
              never appear in the legend or a tooltip. */}
          <Bar dataKey="base" stackId="bridge" fill="transparent" isAnimationActive={false} legendType="none" />
          {/* Every step is labelled. On a linear axis the closing total is a
              few pixels tall against revenue — £226k against £9.2m — so the
              bar alone cannot be read, and this is the one chart where a
              number on every mark earns its place. */}
          <Bar dataKey="delta" stackId="bridge" radius={[4, 4, 0, 0]} legendType="none">
            {data.map((row) => (
              <Cell key={row.name} fill={fillFor(row.kind)} />
            ))}
            <LabelList
              dataKey="signed"
              position="top"
              offset={6}
              formatter={(v: unknown) => fmtAxis(typeof v === 'number' ? v : 0)}
              style={{ fontSize: 11, fill: '#52514e' }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function CashPositionChart({ d }: { d: ChartsData }) {
  const data = toQuarterlyChartData(d.ctx, {
    closingCash: d.closingCash,
    netOperatingCashflow: d.netOperatingCashflow,
    netInvestingCashflow: d.netInvestingCashflow,
    netFinancingCashflow: d.netFinancingCashflow,
  });

  return (
    <ChartCard
      title="Cash Position"
      subtitle="Closing cash, and the flows that moved it"
      currency={d.currency}
      rows={seriesRows(data, [
        { key: 'closingCash', name: 'Closing Cash' },
        { key: 'netOperatingCashflow', name: 'Operating CF' },
        { key: 'netInvestingCashflow', name: 'Investing CF' },
        { key: 'netFinancingCashflow', name: 'Financing CF' },
      ], 'quarter')}
    >
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart {...GROUPED_BARS} data={data} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
          <CartesianGrid {...grid()} />
          <XAxis dataKey="quarter" {...axisProps()} />
          <YAxis tickFormatter={fmtAxis} width={58} {...axisProps()} />
          <Tooltip formatter={(v: number | undefined) => fmtFull(v, d.currency)} {...tooltipProps()} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Line type="monotone" dataKey="closingCash" name="Closing Cash" stroke={COLORS.cash} strokeWidth={2} />
          <Bar dataKey="netOperatingCashflow" name="Operating CF" fill={COLORS.opCashflow} />
          <Bar dataKey="netInvestingCashflow" name="Investing CF" fill={COLORS.invCashflow} />
          <Bar dataKey="netFinancingCashflow" name="Financing CF" fill={COLORS.finCashflow} />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function BalanceSheetChart({ d }: { d: ChartsData }) {
  const data = toQuarterlyChartData(d.ctx, {
    fixedAssetsNet: d.fixedAssetsNet,
    totalCurrentAssets: d.totalCurrentAssets,
    totalLiabilitiesAndEquity: d.totalLiabilitiesAndEquity,
  });

  return (
    <ChartCard
      title="Balance sheet composition"
      subtitle="Where the assets sit at each period end"
      currency={d.currency}
      rows={seriesRows(data, [
        { key: 'totalCurrentAssets', name: 'Current Assets' },
        { key: 'fixedAssetsNet', name: 'Fixed Assets (Net)' },
        ...(d.showBalanceReconciliation
          ? [{ key: 'totalLiabilitiesAndEquity', name: 'Liabilities + Equity' }]
          : []),
      ], 'quarter')}
    >
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart {...SINGLE_BARS} data={data} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
          <CartesianGrid {...grid()} />
          <XAxis dataKey="quarter" {...axisProps()} />
          <YAxis tickFormatter={fmtAxis} width={58} {...axisProps()} />
          <Tooltip formatter={(v: number | undefined) => fmtFull(v, d.currency)} {...tooltipProps()} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="fixedAssetsNet" name="Fixed Assets (Net)" fill={COLORS.fixedAssets} stackId="assets" />
          <Bar dataKey="totalCurrentAssets" name="Current Assets" fill={COLORS.currentAssets} stackId="assets" radius={[4, 4, 0, 0]} />
          {d.showBalanceReconciliation && (
            <Line type="monotone" dataKey="totalLiabilitiesAndEquity" name="Liab + Equity" stroke={COLORS.liabEquity} strokeWidth={2} strokeDasharray="5 5" dot={false} />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function MarginTrendsChart({ d }: { d: ChartsData }) {
  const data = d.ctx.fiscalYears.map((year) => {
    const y: AnnualSummaryYear | undefined = d.annualSummary?.years[year];
    return {
      year: String(year),
      grossMargin: y ? +(y.grossMargin * 100).toFixed(1) : 0,
      ebitdaMargin: y ? +(y.ebitdaMargin * 100).toFixed(1) : 0,
      netMargin: y ? +(y.netMargin * 100).toFixed(1) : 0,
    };
  });

  return (
    <ChartCard
      title="Margin Trends (Annual)"
      subtitle="Gross, EBITDA and net margin as a share of revenue"
      rows={seriesRows(data, [
        { key: 'grossMargin', name: 'Gross Margin' },
        { key: 'ebitdaMargin', name: 'EBITDA Margin' },
        { key: 'netMargin', name: 'Net Margin' },
      ], 'year')}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
          <CartesianGrid {...grid()} />
          <XAxis dataKey="year" {...axisProps()} />
          <YAxis tickFormatter={(v: number) => `${v}%`} tick={{ fontSize: 11 }} />
          <Tooltip formatter={(v: number | undefined) => `${v ?? 0}%`} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Line type="monotone" dataKey="grossMargin" name="Gross Margin" stroke={COLORS.grossMargin} strokeWidth={2} />
          <Line type="monotone" dataKey="ebitdaMargin" name="EBITDA Margin" stroke={COLORS.ebitdaMargin} strokeWidth={2} />
          <Line type="monotone" dataKey="netMargin" name="Net Margin" stroke={COLORS.netMargin} strokeWidth={2} />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function WorkingCapitalChart({ d }: { d: ChartsData }) {
  const data = d.ctx.allQuarters.map((q) => ({
    quarter: fmtQuarter(q),
    tradeDebtors: d.tradeDebtors[q] ?? 0,
    tradeCreditors: d.tradeCreditors[q] ?? 0,
    netWorkingCapital: (d.tradeDebtors[q] ?? 0) - (d.tradeCreditors[q] ?? 0),
  }));

  return (
    <ChartCard
      title="Working Capital"
      subtitle="Debtors and creditors, and the net position they leave"
      currency={d.currency}
      rows={seriesRows(data, [
        { key: 'tradeDebtors', name: 'Trade Debtors' },
        { key: 'tradeCreditors', name: 'Trade Creditors' },
        { key: 'netWorkingCapital', name: 'Net Working Capital' },
      ], 'quarter')}
    >
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart {...GROUPED_BARS} data={data} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
          <CartesianGrid {...grid()} />
          <XAxis dataKey="quarter" {...axisProps()} />
          <YAxis tickFormatter={fmtAxis} width={58} {...axisProps()} />
          <Tooltip formatter={(v: number | undefined) => fmtFull(v, d.currency)} {...tooltipProps()} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="tradeDebtors" name="Trade Debtors" fill={COLORS.debtors} />
          <Bar dataKey="tradeCreditors" name="Trade Creditors" fill={COLORS.creditors} />
          <Line type="monotone" dataKey="netWorkingCapital" name="Net Working Capital" stroke={COLORS.nwc} strokeWidth={2} />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}


/** Shown when a chart's data has not been produced yet. */
function NotYetRun({ what, how }: { what: string; how: string }) {
  return (
    <div className="chart-empty flex h-full items-center justify-center px-6 text-center">
      <p className="text-sm text-gray-500">
        No {what} yet.
        <br />
        <span className="text-xs">{how}</span>
      </p>
    </div>
  );
}

/**
 * The sampled enterprise value, as a range.
 *
 * A valuation is a forecast wearing a decimal point, and this is the chart that
 * says so. The form follows the data: a middle with two edges is a point plus a
 * whisker, never a bar - a bar from zero would imply the value could be zero,
 * and a floating bar would hide where the middle sits.
 *
 * The deterministic DCF is drawn alongside as a reference, because the question
 * a reader actually has is "where does our single number sit in that range?".
 */
function ValuationRangeChart({ d }: { d: ChartsData }) {
  const ev = d.evDistribution;

  // No sampled distribution: show the span the model's own scenarios produce.
  //
  // A scenario span is NOT a probability distribution and the card says so -
  // it is the range between the best and worst of a handful of stated shocks,
  // with no claim about likelihood. But it is a real answer computed from the
  // model, and it is available on every model, where the sampled range
  // required an AI forecast that most models have never had.
  if (!ev && d.scenarioValuations && d.scenarioValuations.length > 0) {
    const values = d.scenarioValuations.map((s) => s.enterpriseValue);
    const base = d.baseValuation ?? null;
    const low = Math.min(...values, base ?? Infinity);
    const high = Math.max(...values, base ?? -Infinity);
    const raw = Math.max(high - low, 1);
    const pad = raw * 0.1;
    const lo = low - pad;
    const span = raw + pad * 2;
    const at = (v: number) => ((v - lo) / span) * 100;
    const worst = d.scenarioValuations.reduce((a, b) =>
      a.enterpriseValue <= b.enterpriseValue ? a : b,
    );
    const best = d.scenarioValuations.reduce((a, b) =>
      a.enterpriseValue >= b.enterpriseValue ? a : b,
    );

    return (
      <ChartCard
        title="Valuation range"
        subtitle={`Scenario range, from ${worst.label} to ${best.label} — not a probability`}
        currency={d.currency}
        rows={[
          { label: '', values: ['Lowest', 'Base case', 'Highest'] },
          { label: 'Enterprise value', values: [low, base ?? 0, high] },
          { label: 'Scenario', values: [worst.label, 'Base Case', best.label] },
        ]}
      >
        <div className="flex h-full flex-col justify-center px-2">
          <div className="mb-2 text-center">
            <div className="text-2xl font-semibold text-gray-900">
              {fmtFull(base ?? 0, d.currency)}
            </div>
            <div className="text-xs text-gray-500">base case</div>
          </div>

          <div className="relative mx-6 mt-8 h-16">
            <div className="absolute top-1/2 h-0.5 w-full -translate-y-1/2 bg-gray-300" />
            <div
              className="absolute top-1/2 h-8 -translate-y-1/2 rounded"
              style={{
                left: at(low) + '%',
                width: at(high) - at(low) + '%',
                background: seriesColor('capital'),
                opacity: 0.25,
              }}
            />
            {base !== null && (
              <div
                className="absolute top-1/2 h-10 w-1 -translate-x-1/2 -translate-y-1/2 rounded"
                style={{ left: at(base) + '%', background: seriesColor('capital') }}
              />
            )}
          </div>

          <div className="mx-6 mt-2 flex justify-between text-xs text-gray-500">
            <span>{fmtAxis(low)}</span>
            <span>{fmtAxis(high)}</span>
          </div>
          <p className="mt-3 px-6 text-center text-xs text-gray-500">
            Run an AI forecast for a sampled range with percentiles.
          </p>
        </div>
      </ChartCard>
    );
  }

  if (!ev) {
    return (
      <ChartCard title="Valuation range" subtitle="Sampled enterprise value, with percentiles">
        <NotYetRun
          what="probabilistic valuation"
          how="Run an AI forecast with the distribution strategy to sample it."
        />
      </ChartCard>
    );
  }

  // A tenth of the span as padding at each end.
  //
  // Scaling p5 to 0% and p95 to 100% put the median hard against an edge
  // whenever the distribution is skewed, which it usually is: on Northgate
  // p5 1.05m, p50 2.39m, p95 2.46m puts the median marker at 95% with two
  // thirds of the chart empty, and the whole card reads as broken rather
  // than as a skewed sample. The padding keeps every marker inside the
  // plot so the shape of the distribution is what the eye picks up.
  const raw = Math.max(ev.p95 - ev.p5, 1);
  const pad = raw * 0.1;
  const lo = ev.p5 - pad;
  const span = raw + pad * 2;
  const pct = (v: number) => ((v - lo) / span) * 100;
  const dcf = d.dcfEnterpriseValue ?? null;
  const dcfInRange = dcf !== null && dcf >= ev.p5 && dcf <= ev.p95;

  return (
    <ChartCard
      title="Valuation range"
      subtitle="Sampled enterprise value: the box is the middle half, the line the 5th to 95th percentile"
      currency={d.currency}
      rows={[
        { label: '', values: ['5th', '25th', 'Median', '75th', '95th'] },
        { label: 'Enterprise value', values: [ev.p5, ev.p25, ev.p50, ev.p75, ev.p95] },
      ]}
    >
      <div className="flex h-full flex-col justify-center px-2">
        <div className="mb-2 text-center">
          <div className="text-2xl font-semibold text-gray-900">{fmtFull(ev.p50, d.currency)}</div>
          <div className="text-xs text-gray-500">median of the sampled paths</div>
        </div>

        <div className="relative mx-6 mt-8 h-16">
          <div className="absolute top-1/2 h-0.5 w-full -translate-y-1/2 bg-gray-300" />
          <div
            className="absolute top-1/2 h-8 -translate-y-1/2 rounded"
            style={{
              left: pct(ev.p25) + '%',
              width: (pct(ev.p75) - pct(ev.p25)) + '%',
              background: seriesColor('capital'),
              opacity: 0.25,
            }}
          />
          <div
            className="absolute top-1/2 h-10 w-1 -translate-x-1/2 -translate-y-1/2 rounded"
            style={{ left: pct(ev.p50) + '%', background: seriesColor('capital') }}
          />
          {dcfInRange && dcf !== null && (
            <div
              className="absolute top-1/2 h-12 w-0.5 -translate-x-1/2 -translate-y-1/2"
              style={{ left: pct(dcf) + '%', background: seriesColor('revenue') }}
            />
          )}
          <span className="absolute -bottom-6 left-0 -translate-x-1/2 text-xs text-gray-500">
            {fmtAxis(ev.p5)}
          </span>
          <span className="absolute -bottom-6 right-0 translate-x-1/2 text-xs text-gray-500">
            {fmtAxis(ev.p95)}
          </span>
        </div>

        <div className="mt-10 flex justify-center gap-4 text-xs text-gray-600">
          <span className="flex items-center gap-1.5">
            <span
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ background: seriesColor('capital') }}
            />
            Sampled range
          </span>
          {dcf !== null && dcfInRange && (
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-3 w-0.5" style={{ background: seriesColor('revenue') }} />
              DCF {fmtFull(dcf, d.currency)}
            </span>
          )}
          {/* Silently dropping the marker hid a real disagreement: on one model
              the DCF was 1.6m against a sampled 4.2-5.2m. If the two methods
              disagree that much, that IS the finding. */}
          {dcf !== null && !dcfInRange && (
            <span className="text-amber-700">
              DCF {fmtFull(dcf, d.currency)} falls outside the sampled range
            </span>
          )}
        </div>
      </div>
    </ChartCard>
  );
}

/**
 * Which assumptions move the valuation, largest first.
 *
 * Sorted by magnitude rather than left in input order: the question is which
 * few drivers matter, so rank IS the information.
 */
/** `revenue_y1` -> `Revenue, year 1`. The pipeline emits raw identifiers. */
function driverLabel(key: string): string {
  const cleaned = key
    .replace(/_terminal$/, ' (terminal)')
    .replace(/_y(\d+)$/, ', year $1')
    .replace(/_/g, ' ');
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

function DriverSensitivityChart({ d }: { d: ChartsData }) {
  const raw = d.driverSensitivity;
  const hasSampled = raw && Object.keys(raw).length > 0;

  // No sampled sensitivity: show what the model's own scenarios do to
  // enterprise value. This is a swing in CURRENCY, not a correlation, so it
  // gets its own axis and its own units rather than being squeezed into the
  // [-1, 1] scale the sampled version uses.
  if (!hasSampled && d.scenarioValuations && d.scenarioValuations.length > 0) {
    const base = d.baseValuation ?? 0;
    const data = d.scenarioValuations
      .map((sv) => ({ name: sv.label, value: sv.enterpriseValue - base }))
      .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
      .slice(0, 8);
    const reach = Math.max(...data.map((r) => Math.abs(r.value)), 1);

    return (
      <ChartCard
        title="What moves the valuation"
        subtitle="Change in enterprise value under each scenario, largest first"
        currency={d.currency}
        rows={[
          { label: '', values: data.map((r) => r.name) },
          { label: 'Change in EV', values: data.map((r) => r.value) },
        ]}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 5, right: 30, bottom: 5, left: 10 }}>
            <CartesianGrid {...gridProps()} horizontal={false} vertical />
            <XAxis
              type="number"
              domain={[-reach, reach]}
              tickFormatter={fmtAxis}
              {...axisProps()}
            />
            <YAxis type="category" dataKey="name" width={150} {...axisProps()} />
            <Tooltip
              formatter={(v: number | undefined) => [fmtFull(v, d.currency), 'Change in EV']}
              {...tooltipProps()}
            />
            {/* Memoised data and no entry animation, for the reason in the
                sampled chart below: a fresh array identity restarts the
                animation and the bars never settle. */}
            <Bar dataKey="value" radius={[0, 4, 4, 0]} legendType="none" isAnimationActive={false}>
              {data.map((row) => (
                <Cell
                  key={row.name}
                  fill={row.value >= 0 ? seriesColor('cash') : seriesColor('cost')}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    );
  }

  if (!hasSampled) {
    return (
      <ChartCard title="What moves the valuation" subtitle="Effect of each driver on enterprise value">
        <NotYetRun what="driver sensitivity" how="Produced alongside the probabilistic forecast." />
      </ChartCard>
    );
  }

  // These are correlation coefficients between a driver and the sampled
  // enterprise value, in [-1, 1] — NOT an amount of money. Formatting them as
  // currency produced an axis reading -1, 0, 0, 1, 1 and tooltips saying "£1".
  // Memoised on the values, not rebuilt per render. Recharts restarts a bar's
  // entry animation whenever the data array's identity changes, and this array
  // was a fresh Object.entries(...).map(...) every time, so the bars were
  // re-animated from zero width before they could settle: five
  // `recharts-bar-rectangle` groups rendered with no shape inside them at all.
  // The bridges above already carry isAnimationActive={false} for the same
  // reason; this fixes the cause rather than only the symptom.
  const data = useMemo(
    () =>
      Object.entries(raw as Record<string, number>)
        .map(([name, value]) => ({ name: driverLabel(name), value }))
        .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
        .slice(0, 8),
    [raw],
  );

  return (
    <ChartCard
      title="What moves the valuation"
      subtitle="How strongly each driver tracks enterprise value, strongest first (-1 to +1)"
      rows={[
        { label: '', values: data.map((r) => r.name) },
        { label: 'Correlation', values: data.map((r) => r.value.toFixed(2)) },
      ]}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 5, right: 30, bottom: 5, left: 10 }}>
          <CartesianGrid {...gridProps()} horizontal={false} vertical />
          <XAxis
            type="number"
            domain={[-1, 1]}
            ticks={[-1, -0.5, 0, 0.5, 1]}
            tickFormatter={(v: number) => v.toFixed(1)}
            {...axisProps()}
          />
          <YAxis type="category" dataKey="name" width={150} {...axisProps()} />
          <Tooltip
            formatter={(v: number | undefined) => [(v ?? 0).toFixed(2), 'Correlation']}
            {...tooltipProps()}
          />
          <Bar
            dataKey="value"
            radius={[0, 4, 4, 0]}
            legendType="none"
            isAnimationActive={false}
          >
            {data.map((row) => (
              <Cell key={row.name} fill={row.value >= 0 ? seriesColor('cash') : seriesColor('cost')} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

/**
 * Opening cash to closing cash, for one year.
 *
 * The cash position chart shows the flows beside the level; this shows how the
 * level got there. Same bridge construction as the P&L.
 */
function CashBridgeChart({ d }: { d: ChartsData }) {
  const years = d.ctx.fiscalYears;
  const [year, setYear] = useState(() => years[years.length - 1]);
  const periods = d.ctx.quartersByYear?.[year] ?? [];

  const sumOver = (series: QuarterlyData) =>
    periods.reduce((acc, q) => acc + (series[q] ?? 0), 0);

  const last = periods[periods.length - 1];
  const closing = d.closingCash[last] ?? 0;
  const operating = sumOver(d.netOperatingCashflow);
  const investing = sumOver(d.netInvestingCashflow);
  const financing = sumOver(d.netFinancingCashflow);
  const opening = closing - operating - investing - financing;

  const steps = [
    { name: 'Opening', amount: opening, kind: 'start' },
    { name: 'Operating', amount: operating, kind: 'flow' },
    { name: 'Investing', amount: investing, kind: 'flow' },
    { name: 'Financing', amount: financing, kind: 'flow' },
  ];

  let running = 0;
  const data = steps.map((st) => {
    const base = st.amount >= 0 ? running : running + st.amount;
    running += st.amount;
    return { name: st.name, base, delta: Math.abs(st.amount), signed: st.amount, kind: st.kind };
  });
  data.push({ name: 'Closing', base: 0, delta: running, signed: running, kind: 'total' });

  const fillFor = (kind: string, signed: number) =>
    kind === 'start' || kind === 'total'
      ? seriesColor('cash')
      : signed >= 0
        ? seriesColor('cash')
        : seriesColor('cost');

  return (
    <ChartCard
      title="How cash moved"
      subtitle={'Opening to closing cash in ' + year}
      currency={d.currency}
      rows={seriesRows(
        data as unknown as Record<string, string | number>[],
        [{ key: 'signed', name: 'Amount' }],
        'name',
      )}
      control={
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-700"
          aria-label="Cash bridge year"
        >
          {years.map((fy) => (
            <option key={fy} value={fy}>
              {fy}
            </option>
          ))}
        </select>
      }
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart {...SINGLE_BARS} data={data} margin={{ top: 18, right: 20, bottom: 5, left: 10 }}>
          <CartesianGrid {...grid()} />
          <XAxis dataKey="name" {...axisProps()} />
          <YAxis tickFormatter={fmtAxis} width={58} {...axisProps()} />
          <Tooltip
            {...tooltipProps()}
            formatter={(_v, _n, item) => [
              fmtFull((item?.payload as { signed?: number })?.signed, d.currency),
              'Amount',
            ]}
          />
          <Bar dataKey="base" stackId="cash" fill="transparent" isAnimationActive={false} legendType="none" />
          <Bar dataKey="delta" stackId="cash" radius={[4, 4, 0, 0]} legendType="none">
            {data.map((row) => (
              <Cell key={row.name} fill={fillFor(row.kind, row.signed)} />
            ))}
            <LabelList
              dataKey="signed"
              position="top"
              offset={6}
              formatter={(v: unknown) => fmtAxis(typeof v === 'number' ? v : 0)}
              style={{ fontSize: 11, fill: '#52514e' }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

/**
 * Working capital in days rather than currency.
 *
 * Absolute debtors grow with the business, so a rising line says nothing about
 * whether collection is getting worse. Days strip the growth out, which is why
 * a credit committee asks for them.
 */
function WorkingCapitalDaysChart({ d }: { d: ChartsData }) {
  const data = d.ctx.fiscalYears.map((year) => {
    const y = d.annualSummary?.years[year];
    const periods = d.ctx.quartersByYear?.[year] ?? [];
    const last = periods[periods.length - 1];
    const revenue = y?.totalRevenue ?? 0;
    const cogs = y?.cogs ?? 0;
    const debtors = d.tradeDebtors[last] ?? 0;
    const creditors = d.tradeCreditors[last] ?? 0;
    const stock = d.stocks?.[last] ?? 0;
    return {
      year: String(year),
      dso: revenue ? Math.round((debtors / revenue) * 365) : 0,
      dpo: cogs ? Math.round((creditors / cogs) * 365) : 0,
      dio: cogs ? Math.round((stock / cogs) * 365) : 0,
    };
  });

  return (
    <ChartCard
      title="Working capital, in days"
      subtitle="Collection, payment and stock days - growth removed"
      rows={seriesRows(
        data,
        [
          { key: 'dso', name: 'Debtor days' },
          { key: 'dpo', name: 'Creditor days' },
          { key: 'dio', name: 'Stock days' },
        ],
        'year',
      )}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
          <CartesianGrid {...grid()} />
          <XAxis dataKey="year" {...axisProps()} />
          <YAxis tickFormatter={(v: number) => v + 'd'} width={58} {...axisProps()} />
          <Tooltip
            formatter={(v: number | undefined) => Math.round(v ?? 0) + ' days'}
            {...tooltipProps()}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Line type="monotone" dataKey="dso" name="Debtor days" stroke={seriesColor('revenue')} strokeWidth={2} />
          <Line type="monotone" dataKey="dpo" name="Creditor days" stroke={seriesColor('cost')} strokeWidth={2} />
          <Line type="monotone" dataKey="dio" name="Stock days" stroke={seriesColor('capital')} strokeWidth={2} />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

/**
 * Net assets: total assets against total liabilities at each period end, and what is left.
 *
 * Baseline's own workbook charted cash beside net assets; cash has its own chart here, so this
 * one shows net assets with the two totals it is the difference of - a fall reads as assets
 * shrinking or liabilities growing, not only as a smaller number.
 */
function NetAssetsChart({ d }: { d: ChartsData }) {
  const data = toQuarterlyChartData(d.ctx, {
    totalAssets: d.totalAssets,
    totalLiabilities: d.totalLiabilities,
    netAssets: d.netAssets,
  });

  return (
    <ChartCard
      title="Net assets"
      subtitle="What the company owns and owes at each period end, and what is left"
      currency={d.currency}
      rows={seriesRows(data, [
        { key: 'totalAssets', name: 'Total assets' },
        { key: 'totalLiabilities', name: 'Total liabilities' },
        { key: 'netAssets', name: 'Net assets' },
      ], 'quarter')}
    >
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart {...GROUPED_BARS} data={data} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
          <CartesianGrid {...grid()} />
          <XAxis dataKey="quarter" {...axisProps()} />
          <YAxis tickFormatter={fmtAxis} width={58} {...axisProps()} />
          <Tooltip formatter={(v: number | undefined) => fmtFull(v, d.currency)} {...tooltipProps()} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="totalAssets" name="Total assets" fill={COLORS.assets} />
          <Bar dataKey="totalLiabilities" name="Total liabilities" fill={COLORS.liabilities} />
          <Line type="monotone" dataKey="netAssets" name="Net assets" stroke={COLORS.netAssets} strokeWidth={2} />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

// ─── Main View ───────────────────────────────────────────────

/**
 * The charts themselves, given data. Shared by both model versions.
 *
 * `accountingOnly` leaves out the two valuation charts: v2 keeps valuation in its own
 * discipline, so an accounting model's charts are the statements' alone. v1 shows all.
 */
export function ChartsGrid({
  data,
  accountingOnly = false,
  padded = true,
}: {
  data: ChartsData;
  accountingOnly?: boolean;
  /** Off where the page around it already pads its tabs (Baseline Accounting's workspace). */
  padded?: boolean;
}) {
  return (
    <div className={padded ? 'h-full overflow-auto p-6' : undefined}>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Ordered by what a reader wants first, not by when each was built.
            The page runs well past one screen, so whatever is distinctive has
            to be above the fold: the valuation range and the two bridges lead,
            and the familiar trend charts follow. */}
        {!accountingOnly && <ValuationRangeChart d={data} />}
        {!accountingOnly && <DriverSensitivityChart d={data} />}
        <PnLBridgeChart d={data} />
        <CashBridgeChart d={data} />
        <RevenueProfitChart d={data} />
        <CashPositionChart d={data} />
        <BalanceSheetChart d={data} />
        <NetAssetsChart d={data} />
        <WorkingCapitalDaysChart d={data} />
        <MarginTrendsChart d={data} />
        <WorkingCapitalChart d={data} />
      </div>
    </div>
  );
}
